import { BitcoinTransactionItem, EUserTier } from 'src/interfaces';
import { usePaginatedTxItems } from './hooks/usePaginatedTxItems';
import { TxIOPagination } from './TxIOPagination';
import { TxIORow } from './TxIORow';
import { useTxContext } from '../../TxContext';
import { useRef, useContext } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AppContext } from 'src/hooks/AppProvider';

interface TxIOTableProps {
  title: string;
  type: 'inputs' | 'outputs';
}

const formatAmount = (amount: number): string => {
  const btcAmount = amount / 100000000;
  const [whole, decimal] = btcAmount.toFixed(8).split('.');
  const formattedWhole = Number(whole).toLocaleString();
  return `${formattedWhole}.${decimal}`;
};

export function TxIOTable({ title, type }: TxIOTableProps) {
  const { transaction } = useTxContext();
  const { user } = useContext(AppContext);
  const tableHeaderRef = useRef<HTMLTableRowElement>(null);

  const initialItems: BitcoinTransactionItem[] =
    type === 'inputs' ? transaction.cpin : transaction.cpout;

  const totalCount =
    type === 'inputs' ? transaction.input_cnt : transaction.output_cnt;

  const txHash = transaction.id;

  const { items, isLoading, hasMore, loadMore, showLess, canShowLess } =
    usePaginatedTxItems({
      initialItems,
      txHash,
      type,
      totalCount,
      scrollTargetRef: tableHeaderRef,
    });

  // Check if user has access to advanced columns (cluster ID and script type)
  const hasAdvancedAccess =
    user?.accountType &&
    [
      EUserTier.BUSINESS,
      EUserTier.PRO,
      EUserTier.ENTERPRISE,
      EUserTier.INTERNAL,
      EUserTier.TRIAL,
    ].includes(user.accountType);

  // Optimize animations for large datasets
  const shouldAnimateRows = items.length <= 100; // Disable row animations for very large lists
  const animationDelay = 0.015; // Much faster stagger for smooth one-by-one effect

  return (
    <div>
      <div className="mb-2 text-sm font-medium">
        {title} ({totalCount})
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-300 bg-gray-50 shadow-sm">
        <table className="w-full table-fixed">
          <thead>
            <tr
              ref={tableHeaderRef}
              className="bg-gray-100 text-xs font-medium text-gray-600"
            >
              <th
                className={
                  hasAdvancedAccess
                    ? 'w-[30%] p-2 text-left'
                    : 'w-[45%] p-2 text-left'
                }
              >
                Address
              </th>
              {hasAdvancedAccess && (
                <th className="w-[30%] p-2 text-left">Cluster ID</th>
              )}
              {hasAdvancedAccess && (
                <th className="w-[15%] p-2 text-left">Script Type</th>
              )}
              <th
                className={
                  hasAdvancedAccess
                    ? 'w-[25%] p-2 text-right'
                    : 'w-[55%] p-2 text-right'
                }
              >
                Amount
              </th>
            </tr>
          </thead>
          <AnimatePresence mode="popLayout">
            <motion.tbody
              key="tbody"
              className="divide-y divide-gray-100"
              initial={shouldAnimateRows ? 'hidden' : false}
              animate={shouldAnimateRows ? 'visible' : false}
              variants={
                shouldAnimateRows
                  ? {
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: animationDelay,
                        },
                      },
                    }
                  : undefined
              }
              layout={shouldAnimateRows}
            >
              {items.map((item) => (
                <TxIORow
                  key={`${item.intxid ?? ''}-${item.n}`}
                  address={item.addr}
                  amount={formatAmount(item.amt)}
                  shouldAnimate={shouldAnimateRows}
                  hasAdvancedAccess={hasAdvancedAccess}
                />
              ))}
            </motion.tbody>
          </AnimatePresence>
          {(hasMore || canShowLess) && (
            <tfoot>
              <tr>
                <td
                  colSpan={hasAdvancedAccess ? 4 : 2}
                  className="border-t border-gray-200 bg-gray-50 p-2"
                >
                  <TxIOPagination
                    totalCount={totalCount}
                    currentCount={items.length}
                    isLoading={isLoading}
                    hasMore={hasMore}
                    canShowLess={canShowLess}
                    loadMore={loadMore}
                    showLess={showLess}
                  />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
